import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { apiFetch } from '../../config/api'
import { Eye, EyeOff, Loader2 } from 'lucide-react'

export default function DashboardLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dbTestResult, setDbTestResult] = useState<string | null>(null)
  const [isTestingDb, setIsTestingDb] = useState(false)
  const navigate = useNavigate()

  if (localStorage.getItem('dashboard_token')) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email || !password) {
      setError('Bitte füllen Sie alle Felder aus.')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        localStorage.setItem('dashboard_token', data.token)
        localStorage.setItem('dashboard_user', JSON.stringify(data.user))
        navigate('/dashboard')
      } else {
        setError(data.error || 'Anmeldung fehlgeschlagen')
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.')
      console.error('Login error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const testDatabaseConnection = async () => {
    setIsTestingDb(true)
    setDbTestResult(null)
    setError('')

    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
      const testUrl = apiBase.startsWith('http')
        ? `${apiBase}/test-db.php`
        : `${apiBase}/test-db.php`

      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const responseText = await response.text()

      if (responseText.trim().startsWith('<?php') || responseText.trim().startsWith('<!')) {
        throw new Error(`PHP wird nicht ausgeführt. Die Datei wird direkt geladen statt über den Router.\n\nAntwort (erste 300 Zeichen):\n${responseText.substring(0, 300)}`)
      }

      let data
      try {
        data = JSON.parse(responseText)
      } catch {
        throw new Error(`Ungültige JSON-Antwort: ${responseText.substring(0, 200)}`)
      }

      if (data.success) {
        const result = `
✅ Verbindung erfolgreich!

Datenbank: ${data.database}
Host: ${data.host}

Tabellen:
${Object.entries(data.tables).map(([table, info]: [string, any]) =>
  `  ${table}: ${info.exists ? `✅ ${info.count} Einträge` : '❌ Nicht gefunden'}`
).join('\n')}

Admin-User: ${data.admin_user_exists ? '✅ Existiert' : '❌ Nicht gefunden'}
${data.admin_user ? `  ID: ${data.admin_user.id}\n  E-Mail: ${data.admin_user.email}` : ''}
        `.trim()
        setDbTestResult(result)
      } else {
        setDbTestResult(`❌ Fehler: ${data.message || data.error}\n\nDetails:\n${JSON.stringify(data, null, 2)}`)
      }
    } catch (err: any) {
      try {
        const testResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/test-connection.php`)
        const testText = await testResponse.text()
        if (testText.includes('success')) {
          const testData = JSON.parse(testText)
          setDbTestResult(`✅ API ist erreichbar, aber /test-db Endpoint nicht gefunden.\n\nAPI Info:\n${JSON.stringify(testData, null, 2)}\n\nMögliche Ursachen:\n- Router.php leitet nicht richtig weiter\n- test-db.php fehlt im api/ Ordner\n- .htaccess Konfiguration`)
        } else {
          throw new Error('Test-Endpoint auch nicht erreichbar')
        }
      } catch {
        setDbTestResult(`❌ Verbindungsfehler: ${err.message || 'Unbekannter Fehler'}\n\nBitte prüfe:\n- Ist die API-URL korrekt? (aktuell: ${import.meta.env.VITE_API_BASE_URL || '/api'})\n- Ist die .htaccess korrekt?\n- Läuft der Backend-Server?\n- Ist router.php vorhanden?`)
      }
      console.error('DB Test error:', err)
    } finally {
      setIsTestingDb(false)
    }
  }

  return (
    <div className="dashboard-shell min-h-screen min-h-[100dvh] grid lg:grid-cols-2 bg-gray-950">
      {/* Visual panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gray-950 border-r border-gray-800">
        <img
          src="/assets/home_pics/HK_184.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-brand-950/45 to-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative z-10 p-10">
          <img src="/Logo_HM.png" alt="Hückels May" className="h-14 w-auto" />
        </div>

        <div className="relative z-10 p-10 max-w-lg">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-brand-300 mb-3">
            Verwaltung
          </p>
          <h1 className="text-4xl font-semibold text-white leading-tight mb-3">
            Hückels May
          </h1>
          <p className="text-zinc-300 text-sm leading-relaxed">
            Events, Brunch und Inhalte an einem Ort steuern.
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-10 lg:px-16 bg-gray-50">
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden mb-10">
            <img src="/Logo_HM.png" alt="Hückels May" className="h-12 w-auto mb-6" />
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-1">Anmelden</h2>
          <p className="text-sm text-gray-500 mb-8">
            Melde dich an, um das Dashboard zu öffnen.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                E-Mail
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full h-11 px-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 text-sm outline-none focus:border-brand-800 focus:ring-2 focus:ring-brand-800/20"
                placeholder="ihre@email.de"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Passwort
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full h-11 px-3 pr-11 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 text-sm outline-none focus:border-brand-800 focus:ring-2 focus:ring-brand-800/20"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-700 rounded-md"
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-11 rounded-lg bg-brand-800 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSubmitting ? 'Wird angemeldet…' : 'Anmelden'}
            </button>
          </form>

          <p className="mt-8 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-900">
              Zurück zur Website
            </Link>
          </p>

          <details className="mt-10 group">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 list-none flex items-center gap-1">
              <span className="group-open:hidden">Technischer Check</span>
              <span className="hidden group-open:inline">Technischer Check schließen</span>
            </summary>
            <div className="mt-3">
              <button
                type="button"
                onClick={testDatabaseConnection}
                disabled={isTestingDb}
                className="w-full h-9 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-medium hover:bg-gray-100 disabled:opacity-50"
              >
                {isTestingDb ? 'Teste Verbindung…' : 'Datenbankverbindung testen'}
              </button>
              {dbTestResult && (
                <pre className="mt-3 text-[11px] text-gray-600 whitespace-pre-wrap font-mono overflow-auto max-h-64 bg-gray-100 border border-gray-200 p-3 rounded-lg">
                  {dbTestResult}
                </pre>
              )}
            </div>
          </details>
        </div>
      </div>
    </div>
  )
}
